/**
 * Defines the DialogInput component, a stylised TextField used throughout the application
 * for dialog forms. It leverages Material-UI's TextField component, customising its appearance
 * and behavior to fit the application's design requirements. The component integrates with the
 * application theme for consistent styling.
 *
 * @module DialogInput
 */

import { TextField, useTheme } from '@mui/material';
import PropTypes from 'prop-types';

/**
 * A stylised input component for use in dialog forms, providing a consistent look and feel.
 * It supports customisation such as labels, placeholders, and additional props.
 *
 * @component
 * @param {Object} props - Props for configuring the DialogInput component
 * @param {string} props.id - The identifier for the input, used for accessibility purposes
 * @param {string} [props.value=''] - The input value
 * @param {string} props.label - The text label for the input field
 * @param {string} [props.placeholder=''] - A placeholder text shown when the input is empty
 * @param {Object} props.props - Additional props to be spread into the TextField component
 * @returns {JSX.Element} The stylised TextField component
 */
export const DialogInput = ({
    id,
    value = '',
    label,
    placeholder = '',
    ...props
}) => {
    const theme = useTheme();

    return (
        <TextField
            id={label}
            hiddenLabel
            label={label}
            placeholder={placeholder}
            size='small'
            value={value}
            variant='outlined'
            fullWidth
            sx={{
                '& .MuiOutlinedInput-root': {
                    borderRadius: 50,
                    minHeight: '28px',
                    minWidth: '50px'
                },
                '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: theme.palette.common.black
                }
            }}
            InputLabelProps={{
                sx: {
                    color: theme.palette.disabled.darkest,
                    '&.MuiFormLabel-root.Mui-disabled': {
                        WebkitTextFillColor: theme.palette.disabled.darkest
                    }
                }
            }}
            inputProps={{ maxLength: 500 }}
            onKeyDown={(e) => {
                e.stopPropagation();
            }}
            {...props}
        />
    );
};

DialogInput.propTypes = {
    id: PropTypes.string.isRequired,
    value: PropTypes.string,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    required: PropTypes.bool,
    helperText: PropTypes.string
};
